<template>
  <div class="bg-white d-flex">
    <ion-button
      id="open-modal-book"
      fill="solid"
      expand="block"
      size="default"
      color="primary"
      class="text-lg text-weight-bold"
    >
      {{ t('announcement.bookNow') }}
    </ion-button>

    <ion-modal
      ref="bookModal"
      trigger="open-modal-book"
      :can-dismiss="canDismiss"
      :presenting-element="presentingElement"
      @willPresent="willPresent"
      :initial-breakpoint="breakpoint"
      :breakpoints="[0.25, 0.5, 0.75, 0.95]"
    >
      <swiper @swiper="onSwiper" class="swiper bg-white">
        <swiper-slide class="swiper-no-swiping" v-if="!isProfileComplete || !hasDocuments">
          <div class="bg-white">
            <ProfileEdit
              hide-close-button
              show-continue-button
              :disable-continue-button="!hasDocuments"
              @continue="handleSlideTo(currentStep + 1)"
            />
          </div>
        </swiper-slide>
        <!-- <swiper-slide v-if="additionalList.length !== 0" class="swiper-no-swiping">
          <div class="block bg-white">
            <AdditionalServicesList @continue="handleSlideTo(currentStep + 1)">
              <template #prepend>
                <PersonalizeProgress :current-step="currentStep + 1" :total-steps="totalSteps" />
              </template>
            </AdditionalServicesList>
          </div>
        </swiper-slide> -->
        <!-- <swiper-slide class="swiper-no-swiping">
          <div class="block bg-white" v-if="!isLoading">
            <DocumentListForm
              :rental-unit="rentalUnit"
              @back="handleSlideTo(currentStep - 1)"
              @continue="handleSlideTo(currentStep + 1)"
              @close="onclose"
              @refresh-documents="handleGetTenantsDocuments()"
            >
              <template #prepend>
                <PersonalizeProgress :current-step="currentStep + 1" :total-steps="totalSteps" />
              </template>
            </DocumentListForm>
          </div>
        </swiper-slide> -->
        <swiper-slide class="swiper-no-swiping">
          <div class="block bg-white">
            <BillingInformationForm
              :rental-unit="rentalUnit"
              @back="handleSlideTo(currentStep - 1)"
              @continue="handleSlideTo(currentStep + 1)"
              @close="onclose"
            >
              <template #prepend>
                <PersonalizeProgress :current-step="currentStep + 1" :total-steps="totalSteps" />
              </template>
            </BillingInformationForm>
          </div>
        </swiper-slide>
        <swiper-slide class="swiper-no-swiping">
          <div class="block bg-white">
            <VerifyBookingData
              :rental-unit="rentalUnit"
              @back="handleSlideTo(currentStep - 1)"
              @close="onclose"
            >
              <template #prepend>
                <PersonalizeProgress :current-step="currentStep + 1" :total-steps="totalSteps" />
              </template>
            </VerifyBookingData>
          </div>
        </swiper-slide>
      </swiper>
      <ion-progress-bar v-if="isLoading" type="indeterminate"></ion-progress-bar>
    </ion-modal>
    <ion-modal
      id="profile-document"
      :is-open="toggleDocument"
      :initial-breakpoint="0.7"
      @will-dismiss="toggleDocument = false"
    >
      <div class="ion-padding bg-white h-100">
        <DocumentListForm hide-return></DocumentListForm>
      </div>
    </ion-modal>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n';
  import { IonButton, IonModal, actionSheetController, IonProgressBar } from '@ionic/vue';
  import { Swiper, SwiperSlide } from 'swiper/vue';
  import { computed, onMounted, ref } from 'vue';
  import VerifyBookingData from '../booking/VerifyBookingData.vue';
  import { RentalUnit } from '@/interfaces/elastic/elastic.interface';
  import PersonalizeProgress from '../personalize/PersonalizeProgress.vue';
  import { useToast } from '@/composables/useToast';
  import { storeToRefs } from 'pinia';
  import { useBookingStore } from '@/stores/booking-store';
  import BillingInformationForm from '../booking/BillingInformationForm.vue';
  import DocumentListForm from '../booking/DocumentListForm.vue';
  import { useProfile } from '@/composables/useProfile';
  import ProfileEdit from '@/components/profile/ProfileEdit.vue';
  import { useProfileStore } from '@/stores/profile-store';
  import { useAuth } from '@/composables/useAuth';

  const { showToast } = useToast();
  const { t } = useI18n();
  const { handleGetDocuments, handleGetProfileLocation, toggleDocument } = useProfile();
  const { documents } = storeToRefs(useProfileStore());
  const { getTenantBillingData, getAdditionalCosts } = useBookingStore();
  const { isLoading, rentalUnitSelected } = storeToRefs(useBookingStore());
  const { user } = useAuth();

  const requiredFields = [
    'name',
    'lastName',
    'prefixPhone',
    'phoneNumber',
    'birthday',
    'Nationality',
    'documentType',
    'documentId',
  ];

  const isProfileComplete = computed(() => {
    if (!user.value) {
      return false;
    }
    return (
      requiredFields.every((field) => user.value?.[field as keyof typeof user.value]) &&
      user.value.Location?.fullAddress
    );
  });

  const hasDocuments = computed(() => {
    let isValidDocument = false;
    documents.value.forEach((document) => {
      document.Inputs.forEach((input) => {
        if (input.type === 'file' && input.Answers.length > 0) {
          isValidDocument = true;
        }
      });
    });
    return isValidDocument;
  });

  const bookModal = ref();
  const onclose = () => {
    canDismissOverride.value = true;
    bookModal.value.$el.dismiss(null, 'cancel');
  };
  const totalSteps = ref(2);
  const breakpoint = ref(0.95);

  const canDismissOverride = ref(false);
  const presentingElement = ref(undefined);
  const page = ref();

  const canDismiss = async () => {
    if (canDismissOverride.value) {
      return true;
    }
    const actionSheet = await actionSheetController.create({
      header: 'Are you sure?',
      buttons: [
        {
          text: 'Yes',
          role: 'confirm',
        },
        {
          text: 'No',
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const { role } = await actionSheet.onWillDismiss();
    if (role === 'confirm') {
      canDismissOverride.value = true;
    }

    return role === 'confirm';
  };

  const willPresent = () => {
    canDismissOverride.value = false;
  };

  const props = withDefaults(
    defineProps<{
      rentalUnit: RentalUnit;
    }>(),
    {},
  );

  const swiperInstance = ref();
  const selectedSwiper = ref(0);
  const currentStep = ref(0);

  const handleSlideTo = (idx: number) => {
    if (idx >= 0) {
      /* breakpoint.value = 0.75; */
      selectedSwiper.value = idx;
      swiperInstance.value?.slideTo(selectedSwiper.value);
      currentStep.value = idx;
    }
  };

  const onSwiper = (swiper: any) => {
    swiperInstance.value = swiper;
    swiperInstance.value?.slideTo(selectedSwiper.value);
  };

  const getAdditionalCostsList = async () => {
    try {
      await getAdditionalCosts(props.rentalUnit.Owner?.id as string);
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  // const handleGetTaxesRentalUnit = async () => {
  //   if (props.rentalUnit && props.rentalUnit.Property) {
  //     try {
  //       await getTaxes(props.rentalUnit.Property.InvoicingCentre?.id as string);
  //     } catch (error) {
  //       await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
  //     }
  //   }
  // };

  const handleGetTenantsDocuments = async () => {
    try {
      await handleGetDocuments();
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  const handleGetProfileBillingData = async () => {
    try {
      await getTenantBillingData();
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  const initialConfig = async () => {
    rentalUnitSelected.value = props.rentalUnit;

    isLoading.value = true;
    await getAdditionalCostsList();
    await handleGetTenantsDocuments();
    await handleGetProfileBillingData();
    await handleGetProfileLocation();
    /* await handleGetTaxesRentalUnit(); */

    /* if (additionalList.value.length === 0) {
      totalSteps.value = 3;
    } */

    isLoading.value = false;
  };

  onMounted(() => {
    initialConfig();
    presentingElement.value = page.value;
  });
</script>

<style lang="css" scoped>
  .block {
    width: 100%;
    height: 100%;
    display: flex;
    padding: 40px 24px;
    overflow: auto;
    text-align: start;
  }

  .swiper {
    width: 100% !important;
    height: 100%;
  }

  ion-modal {
    --height: auto;
  }
</style>
